/**
 * Talking to the Worker from the browser: the bridge, and the calls that go through it.
 *
 * The dashboard is a static bundle holding the Supabase anon key and nothing else. The Worker's
 * `/admin/*` routes want `ADMIN_TOKEN` in a header and send no CORS headers, on purpose - that
 * token is the key to every service-role-backed write in the system. So anything that needs the
 * Worker goes through the Supabase Edge Function in `supabase/functions/brain`, which verifies
 * the caller's own session and their place in `app_allowlist`, then forwards to exactly one
 * route named in its own action map.
 *
 * Two rules, kept in one place because every call here has to obey both:
 *
 *  - **Never swallow the reason.** A refused scan or a refused rewrite carries the Worker's own
 *    explanation ("the lane mode is off", "no scan input configured", "this draft is sent"), and
 *    it arrives as a non-2xx, which `functions.invoke` reports as a generic `FunctionsHttpError`
 *    unless the body is read back out of `error.context`. Printing "Edge Function returned a
 *    non-2xx status code" instead would make every refusal undiagnosable, which is the failure
 *    this codebase keeps writing comments about.
 *
 *  - **Never claim success.** `ok: true` means the Worker answered, nothing more. What it
 *    answered with is the caller's business - a scan that started four runs and skipped three is
 *    a success *with* skips, and the UI says both.
 */

import { supabase } from './supabase';

export interface BridgeResult<T> {
  ok: boolean;
  data?: T;
  error?: string;
  /**
   * True when there was no response at all - the request never reached a function.
   *
   * Worth separating from a refusal, because the fix is different: a refusal means the function
   * answered and is asking for something, and this means the function is not there.
   */
  unreachable?: boolean;
}

/**
 * What to say when nothing answered.
 *
 * supabase-js reports this as "Failed to send a request to the Edge Function", which is both
 * literally true and useless: a function that was never deployed, a blocked CORS preflight and an
 * offline laptop all produce that same sentence. The bridge function is the single point of
 * failure for every Worker-backed thing on this dashboard - Scan now, Update reply and the reply
 * prompts - and it is the one setup step whose absence is invisible until you click something, so
 * the message has to name it rather than describe the symptom.
 */
function unreachableMessage(): string {
  return (
    'The dashboard could not reach the bridge function, so nothing was sent to the Worker. ' +
    'It has probably never been deployed - see README step 6, `supabase functions deploy brain`. ' +
    'Scan now, Update reply and the reply prompts all go through it; everything else on this page ' +
    'reads the database directly and is unaffected.'
  );
}

/**
 * POST one action, by whichever transport this build has.
 *
 * `import.meta.env.DEV` is a compile-time constant, so a production bundle contains only the
 * second call and none of the first one's code - the dev route below does not exist to be reached.
 */
export async function invoke<T>(
  body: { action: string } & Record<string, unknown>,
): Promise<BridgeResult<T>> {
  return import.meta.env.DEV ? invokeViaDevServer<T>(body) : invokeViaBridgeFunction<T>(body);
}

/**
 * Development: the Vite dev server answers this itself (`apps/dashboard/vite.dev-brain.ts`), doing
 * the bridge function's job in-process with the token read from the root `.env.local`.
 *
 * The request is same-origin, so no CORS is involved and no deploy is needed - which is the whole
 * point, because the six-step setup in README section 6 is what makes these buttons dead on a
 * laptop. Nothing about this path exists in a build.
 */
async function invokeViaDevServer<T>(
  body: { action: string } & Record<string, unknown>,
): Promise<BridgeResult<T>> {
  const { action, ...params } = body;

  let response: Response;
  try {
    response = await fetch(`/brain/${action}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(params),
    });
  } catch {
    return {
      ok: false,
      error: 'The dev server did not answer at all. Is `npm run dev` still running?',
      unreachable: true,
    };
  }

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const detail = (payload as { error?: string } | null)?.error;
    return { ok: false, error: detail ?? `The dev Worker proxy answered ${response.status}.` };
  }

  // A 200 that is not JSON is Vite's SPA fallback serving index.html, which is what an unregistered
  // `/brain` middleware looks like from here. Naming it beats handing the caller an undefined.
  if (payload === null) {
    return {
      ok: false,
      error:
        'The dev Worker proxy answered with something that is not JSON - the /brain middleware is not registered (see apps/dashboard/vite.dev-brain.ts).',
      unreachable: true,
    };
  }

  return { ok: true, data: payload as T };
}

/**
 * Production: the Supabase Edge Function, which verifies the caller's session and allowlist row
 * and then forwards to one named route. Its own words are kept on failure, including refusals -
 * see `unreachableMessage` for the one case that has none.
 */
async function invokeViaBridgeFunction<T>(body: Record<string, unknown>): Promise<BridgeResult<T>> {
  const { data, error } = await supabase.functions.invoke('brain', { body });

  if (error) {
    const context = (error as { context?: Response }).context;

    // No response to read means the request never arrived. This is checked by name as well as by
    // shape, because the name is the one thing supabase-js is reliable about here.
    if (error.name === 'FunctionsFetchError' || !context || typeof context.clone !== 'function') {
      return { ok: false, error: unreachableMessage(), unreachable: true };
    }

    let detail: string | null = null;

    try {
      const payload = (await context.clone().json()) as { error?: string };
      detail = typeof payload?.error === 'string' ? payload.error : null;
    } catch {
      // Not JSON - an HTML error page from a gateway, most likely. Fall through to the library's
      // own message rather than inventing one.
      detail = null;
    }

    return { ok: false, error: detail ?? error.message };
  }

  return { ok: true, data: data as T };
}

/* ---------------------------------------------------------------------------
   The login - pages/Login.tsx, and the dev auto sign-in in App.tsx
   ------------------------------------------------------------------------- */

/** What the Worker answers a correct login with: a real Supabase session, for the
    dashboard's own account. Never the credentials - the account pair stays on the server. */
export interface SessionPayload {
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  token_type?: string;
}

/**
 * The login is the one call that cannot use `invoke` in a build.
 *
 * `invoke` reaches the Worker through the Supabase Edge Function, and that function verifies a
 * Supabase session before it forwards - which is exactly what a caller with no session does not
 * have. So this goes to `POST /api/login` on the Worker directly: the one route that sends CORS
 * headers, so a page on Pages is allowed to read the answer. Under `vite dev` it takes the same
 * `/brain/*` route as every other call, where the dev server is same-origin and forwards to the
 * local Worker in Node.
 */
async function loginViaWorker(username: string, password: string): Promise<BridgeResult<SessionPayload>> {
  const base = (import.meta.env.VITE_BRAIN_URL as string | undefined)?.replace(/\/+$/, '');
  if (!base) {
    return {
      ok: false,
      error:
        'This build has no VITE_BRAIN_URL, so there is nothing to log in to. Set it to the deployed ' +
        'Worker (the same URL as BRAIN_URL in .env.local) in the Pages project settings and rebuild.',
    };
  }

  let response: Response;
  try {
    response = await fetch(`${base}/api/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
  } catch {
    return {
      ok: false,
      error: `The Worker did not answer at ${base}. Check that it is deployed and that the URL is right.`,
      unreachable: true,
    };
  }

  const payload = (await response.json().catch(() => null)) as (SessionPayload & { error?: string }) | null;

  if (!response.ok) {
    return { ok: false, error: payload?.error ?? `The Worker answered ${response.status}.` };
  }
  if (!payload?.access_token || !payload?.refresh_token) {
    return { ok: false, error: 'The Worker answered the login without a session in it.' };
  }
  return { ok: true, data: payload };
}

/**
 * Log in and keep the session, in one step - what both the form and the dev auto sign-in do.
 *
 * `setSession` is what makes the rest of this app work unchanged: the session it stores is an
 * ordinary one, so every read still goes through RLS, realtime still delivers, and the bridge
 * above still has an Authorization header to send.
 */
export async function signIn(
  username: string,
  password: string,
): Promise<{ ok: boolean; error?: string }> {
  const result = import.meta.env.DEV
    ? await invokeViaDevServer<SessionPayload>({ action: 'login', username, password })
    : await loginViaWorker(username, password);

  if (!result.ok || !result.data) {
    return { ok: false, error: result.error ?? 'The login answered without a session.' };
  }

  const { access_token, refresh_token } = result.data;
  const { error } = await supabase.auth.setSession({ access_token, refresh_token });
  if (error) return { ok: false, error: `Supabase refused that session: ${error.message}` };
  return { ok: true };
}

/* ---------------------------------------------------------------------------
   Reply prompts - Setup, "Reply prompts"
   ------------------------------------------------------------------------- */

export interface PromptsView {
  /** The built-in prompt version, e.g. `draft.v6`; an edited prompt adds a fingerprint to it. */
  version: string;
  /** The operator's replacement, or null when the built-in text is in use. */
  override: string | null;
  /** Exactly what the drafter is given as its system prompt - built-in, or replaced. */
  effective: string;
}

/**
 * The prompts as the drafter resolves them right now.
 *
 * The built-in text lives in the Worker's source and the edits live on the settings row, and
 * only the Worker can merge the two the way the drafter does. *Reading* this rather than
 * keeping a copy of the defaults in this bundle is the whole point: a copy would drift, and the
 * page would then be showing the operator a prompt that nothing is using - the one failure that
 * looks like "my edit did nothing" instead of like a bug.
 */
export function fetchPrompts(): Promise<BridgeResult<PromptsView>> {
  return invoke<PromptsView>({ action: 'prompts' });
}

/* ---------------------------------------------------------------------------
   Update reply - the review queue
   ------------------------------------------------------------------------- */

export interface RedraftResult {
  draftId: string;
  status: 'redrafted' | 'missing_draft' | 'not_open' | 'missing_item' | 'missing_source';
  /** The Worker's reason, for every status other than `redrafted`. */
  note?: string;
  body?: string;
  promptVersion?: string;
  model?: string;
  costGbp?: number;
  areas?: string[];
  /** True when an operator's edit was discarded to make room for the newly generated reply. */
  replacedEdit?: boolean;
}

/**
 * Rewrite one draft with the prompt that is current now.
 *
 * Only `pending` and `approved` drafts can be rewritten - a sent draft has a delivery row behind
 * it and a skipped one is a decision already taken. The Worker enforces that and answers with
 * `status: 'not_open'` plus a note, which is what the toast shows.
 */
export function redraftDraft(draftId: string): Promise<BridgeResult<RedraftResult>> {
  return invoke<RedraftResult>({ action: 'redraft', draftId });
}
